import { CommonModule } from '@angular/common'
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { BaseComponent } from '../../shared'
import { PurchasingDocument, PurchasingOrder, PurchasingReceipt, PurchasingReceiptLineItem } from '../../shared/data-models'
import { FeatureType } from '../../shared/enums'

interface PoOption {
    id: string
    poNumber: string
    vendorName: string
    job: string
    equipment: string
    workOrder: string
    status: string
}

@Component({
    selector: 'app-purchasing-receiving',
    templateUrl: './purchasing-receiving.component.html',
    styleUrls: ['./purchasing-receiving.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink],
})
export class PurchasingReceivingComponent extends BaseComponent implements OnInit {
    @ViewChild('slipInput') slipInput!: ElementRef<HTMLInputElement>
    @ViewChild('docInput') docInput!: ElementRef<HTMLInputElement>

    public receipts: PurchasingReceipt[] = []
    public selectedReceipt: PurchasingReceipt | null = null

    public openOrders: PoOption[] = []
    public selectedOrderId = ''

    public editing = false
    public creating = false
    public editModel: PurchasingReceipt | null = null

    public emailing = false
    public emailModel = { toEmail: '', ccEmail: '', subject: '', message: '' }
    public pendingDiscrepancyReceipt: PurchasingReceipt | null = null

    public isProcessingAi = false
    public isSaving = false

    public readonly statuses = ['Draft', 'Posted']
    public readonly issueTypes = ['OK', 'Short', 'Over', 'Damaged']

    public get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.PurchasingReceivingEdit])
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router
    ) {
        super()
        this.isInitialLoading = false
    }

    ngOnInit(): void {
        this.loadReceipts()
        this.loadOpenOrders()

        this.route.queryParamMap.subscribe(params => {
            const orderId = params.get('orderId')
            if (orderId) {
                this.startReceiveForOrder(orderId)
            }
        })
    }

    private loadReceipts(): void {
        this.services.data.http.get<PurchasingReceipt[]>('/api/PurchasingReceipt').subscribe({
            next: result => (this.receipts = result || []),
            error: () => this.services.notification.error('Failed to load receipts'),
        })
    }

    private loadOpenOrders(): void {
        this.services.data.http.get<PurchasingOrder[]>('/api/PurchasingOrder').subscribe({
            next: result => {
                const receivable = ['Issued', 'Acknowledged', 'PartiallyReceived', 'Received']
                this.openOrders = (result || [])
                    .filter(o => receivable.includes(o.status))
                    .map(o => ({
                        id: o.id,
                        poNumber: o.poNumber,
                        vendorName: o.vendorName,
                        job: o.job,
                        equipment: o.equipment,
                        workOrder: o.workOrder,
                        status: o.status,
                    }))
            },
            error: () => {},
        })
    }

    public selectReceipt(receipt: PurchasingReceipt): void {
        this.editing = false
        this.creating = false
        this.editModel = null
        this.services.data.http.get<PurchasingReceipt>(`/api/PurchasingReceipt?id=${receipt.id}`).subscribe({
            next: result => (this.selectedReceipt = result),
            error: () => this.services.notification.error('Failed to load receipt'),
        })
    }

    public startNew(): void {
        this.selectedReceipt = null
        this.creating = true
        this.editing = true
        this.selectedOrderId = ''
        this.editModel = {
            id: '00000000-0000-0000-0000-000000000000',
            status: 'Draft',
            source: 'Manual',
            receivedDate: new Date(),
            lineItems: [],
            documents: [],
        } as unknown as PurchasingReceipt
    }

    public startReceiveForOrder(orderId: string): void {
        this.startNew()
        this.selectedOrderId = orderId
        this.onOrderSelected()
    }

    public onOrderSelected(): void {
        if (!this.editModel || !this.selectedOrderId) return

        const order = this.openOrders.find(o => o.id === this.selectedOrderId)
        this.editModel.purchaseOrderId = this.selectedOrderId
        this.editModel.poNumber = order?.poNumber || ''
        this.editModel.vendorName = order?.vendorName || ''
        this.editModel.job = order?.job || ''
        this.editModel.equipment = order?.equipment || ''
        this.editModel.workOrder = order?.workOrder || ''

        this.services.data.http
            .get<PurchasingReceiptLineItem[]>(`/api/PurchasingReceipt/GetReceivableLines?orderId=${this.selectedOrderId}`)
            .subscribe({
                next: lines => {
                    if (!this.editModel) return
                    this.editModel.lineItems = (lines || []).map(l => ({
                        ...l,
                        id: '00000000-0000-0000-0000-000000000000',
                        quantityReceived: undefined,
                        issueType: 'OK',
                    })) as PurchasingReceiptLineItem[]
                },
                error: () => this.services.notification.error('Failed to load receivable lines'),
            })
    }

    public startEdit(): void {
        if (!this.selectedReceipt) return
        this.creating = false
        this.editing = true
        this.selectedOrderId = this.selectedReceipt.purchaseOrderId
        this.editModel = JSON.parse(JSON.stringify(this.selectedReceipt))
    }

    public cancelEdit(): void {
        this.editing = false
        this.creating = false
        this.editModel = null
        this.router.navigate([], { relativeTo: this.route, queryParams: {} })
    }

    public save(): void {
        if (!this.editModel || !this.editModel.purchaseOrderId) {
            this.services.notification.error('Select a purchase order')
            return
        }

        const linesToSave = (this.editModel.lineItems || []).filter(l => (l.quantityReceived ?? 0) > 0)
        if (linesToSave.length === 0) {
            this.services.notification.error('Enter at least one received quantity')
            return
        }

        const payload = { ...this.editModel, lineItems: linesToSave, status: 'Posted' }
        this.isSaving = true

        const request = this.creating
            ? this.services.data.http.post<PurchasingReceipt>('/api/PurchasingReceipt', payload)
            : this.services.data.http.put<PurchasingReceipt>('/api/PurchasingReceipt', payload)

        request.subscribe({
            next: result => {
                this.isSaving = false
                this.services.notification.success('Receipt saved')
                this.editing = false
                this.creating = false
                this.editModel = null
                this.loadReceipts()
                this.loadOpenOrders()
                this.router.navigate([], { relativeTo: this.route, queryParams: {} })
                this.selectReceipt(result)

                if (this.hasDiscrepancies(result)) {
                    this.pendingDiscrepancyReceipt = result
                    this.openOverageEmail(result)
                }
            },
            error: () => {
                this.isSaving = false
                this.services.notification.error('Failed to save receipt')
            },
        })
    }

    public deleteReceipt(receipt: PurchasingReceipt): void {
        if (!confirm(`Archive receipt ${receipt.receiptNumber || ''}?`)) return
        this.services.data.http.delete(`/api/PurchasingReceipt/${receipt.id}`).subscribe({
            next: () => {
                this.services.notification.success('Receipt archived')
                if (this.selectedReceipt?.id === receipt.id) this.selectedReceipt = null
                this.loadReceipts()
                this.loadOpenOrders()
            },
            error: () => this.services.notification.error('Failed to archive receipt'),
        })
    }

    public triggerSlipScan(): void {
        if (!this.selectedOrderId && !this.editModel?.purchaseOrderId) {
            this.services.notification.error('Select a purchase order first')
            return
        }
        this.slipInput.nativeElement.click()
    }

    public onSlipFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement
        if (!input.files || input.files.length === 0) return
        const file = input.files[0]
        input.value = ''

        const orderId = this.selectedOrderId || this.editModel?.purchaseOrderId
        if (!orderId) return

        this.isProcessingAi = true
        const formData = new FormData()
        formData.append('file', file, file.name)
        this.services.data.http
            .post<PurchasingReceipt>(`/api/PurchasingReceipt/ExtractFromPackingSlip?orderId=${orderId}`, formData)
            .subscribe({
                next: result => {
                    this.isProcessingAi = false
                    this.editing = true
                    this.creating = true
                    this.selectedOrderId = orderId
                    this.editModel = result
                    if (!this.editModel.lineItems) this.editModel.lineItems = []
                    if (!this.editModel.documents) this.editModel.documents = []
                    this.services.notification.success('Extracted packing slip — review and save')
                },
                error: err => {
                    this.isProcessingAi = false
                    this.services.notification.error(err.error?.error || 'Failed to extract packing slip')
                },
            })
    }

    public triggerDocUpload(): void {
        this.docInput.nativeElement.click()
    }

    public onDocFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement
        const target = this.selectedReceipt
        if (!input.files || input.files.length === 0 || !target) return

        const formData = new FormData()
        formData.append('id', target.id)
        formData.append('documentType', 'PackingSlip')
        for (let i = 0; i < input.files.length; i++) {
            formData.append('files', input.files[i], input.files[i].name)
        }
        input.value = ''

        this.services.data.http.post<PurchasingDocument[]>('/api/PurchasingReceipt/UploadDocuments', formData).subscribe({
            next: result => {
                if (this.selectedReceipt) this.selectedReceipt.documents = result || []
                this.services.notification.success('Document uploaded')
            },
            error: () => this.services.notification.error('Failed to upload document'),
        })
    }

    public openOverageEmail(receipt?: PurchasingReceipt): void {
        const target = receipt || this.selectedReceipt
        if (!target) return
        this.pendingDiscrepancyReceipt = target
        this.emailModel = {
            toEmail: '',
            ccEmail: '',
            subject: `Delivery discrepancy - PO ${target.poNumber || ''} / Receipt ${target.receiptNumber || ''}`.trim(),
            message: '',
        }
        this.emailing = true
    }

    public sendOverageEmail(): void {
        const receipt = this.pendingDiscrepancyReceipt || this.selectedReceipt
        if (!receipt) return

        const payload = {
            receiptId: receipt.id,
            toEmail: this.emailModel.toEmail,
            ccEmail: this.emailModel.ccEmail,
            subject: this.emailModel.subject,
            message: this.emailModel.message,
        }

        this.services.data.http.post('/api/PurchasingReceipt/EmailOverageToVendor', payload).subscribe({
            next: () => {
                this.services.notification.success('Discrepancy email sent to vendor')
                this.emailing = false
                this.pendingDiscrepancyReceipt = null
            },
            error: err => this.services.notification.error(err.error?.error || 'Failed to send email'),
        })
    }

    public hasDiscrepancies(receipt: PurchasingReceipt): boolean {
        return (receipt.lineItems || []).some(l => {
            const issue = (l.issueType || '').toLowerCase()
            return issue === 'over' || issue === 'short' || issue === 'damaged'
        })
    }

    public lineReceiveClass(line: PurchasingReceiptLineItem): string {
        const ordered = line.quantityOrdered ?? 0
        const toDate = line.quantityReceivedToDate ?? 0
        const receiveNow = line.quantityReceived ?? 0
        const cumulative = toDate + receiveNow

        if (receiveNow <= 0) return ''
        if (cumulative > ordered) return 'table-danger'
        if (cumulative === ordered) return 'table-success'
        return 'table-warning'
    }

    public statusBadgeClass(status: string): string {
        switch ((status || '').toLowerCase()) {
            case 'draft':
                return 'bg-secondary'
            case 'posted':
                return 'bg-success'
            default:
                return 'bg-secondary'
        }
    }

    public documentUrl(doc: PurchasingDocument): string {
        return `/Resources/Images/${doc.url}`
    }
}
